import { Form, Spin } from 'antd';
import { useAtomValue } from 'jotai';
import { useCallback, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { familyService } from 'services';
import { getNextStep, getPreviousStep, stepConfigAtom, isChildSchoolAge } from '../../atoms';
import { FAMILY_APPLICATION_STEPS } from '../../familyApplicationConstants';
import PageMeta from 'components/PageMeta/PageMeta';
import { Banner } from 'components/ui/Banner';
import { getFirstName } from 'utils';
import { ApplicationWizardActions } from 'components/application-wizards/ApplicationWizardActions';
import RadioCards from 'components/RadioCards/RadioCards';

const DAYS_OF_WEEK_OPTIONS = [
  { value: 'Monday', label: 'Monday' },
  { value: 'Tuesday', label: 'Tuesday' },
  { value: 'Wednesday', label: 'Wednesday' },
  { value: 'Thursday', label: 'Thursday' },
  { value: 'Friday', label: 'Friday' },
];

export function FamilyApplicationChildrenScheduleOfCare({
  application,
  setApplication,
  loading,
  setStep,
  readOnly = false,
}) {
  const stepConfig = useAtomValue(stepConfigAtom);
  const location = useLocation();

  const [form] = Form.useForm();
  const [submitting, setSubmitting] = useState(false);
  const [scheduleId, setScheduleId] = useState(null);

  const urlParams = new URLSearchParams(location.search);
  const currentChildIndex = parseInt(urlParams.get('entity') || '0', 10);

  const currentChild = application?.children?.[currentChildIndex];
  const childFirstName = getFirstName({ firstName: currentChild?.firstName });

  const CARE_TYPE_LABELS = {
    BEFORE_SCHOOL: 'before school care',
    AFTER_SCHOOL: 'after school care',
    BEFORE_AND_AFTER_SCHOOL: 'before and after school care',
    SUMMER: 'summer care',
  };
  const isSchoolAgeInSchool = isChildSchoolAge(currentChild) && currentChild?.inSchool === true;
  const careTypeCode = currentChild?.schedules?.[0]?.careNeedType?.code;
  const careTypeLabel = isSchoolAgeInSchool ? CARE_TYPE_LABELS[careTypeCode] : null;
  const bannerQuestion = careTypeLabel
    ? `Which days does ${childFirstName} need ${careTypeLabel}?`
    : `Which days does ${childFirstName} need care?`;

  // Pre-populate from schedules already on the child object
  useEffect(() => {
    const existing = currentChild?.schedules?.[0];
    if (existing) {
      setScheduleId(existing.id);
      form.setFieldsValue({ days: existing.days?.map((d) => d.dayOfWeek) || [] });
    }
  }, [currentChild, form]);

  const handleSubmit = useCallback(
    async (values) => {
      const _goToNextStep = (_newApplication) => {
        const nextStep = getNextStep(
          FAMILY_APPLICATION_STEPS.CHILDREN_SCHEDULE_OF_CARE,
          _newApplication,
          stepConfig,
          currentChildIndex,
        );
        setStep({ applicationId: _newApplication.id, newStep: nextStep });
      };

      if (readOnly) {
        _goToNextStep(application);
        return;
      }

      try {
        setSubmitting(true);

        const existingSchedule = currentChild?.schedules?.[0];
        const isSchoolInSchool = isChildSchoolAge(currentChild) && currentChild?.inSchool === true;
        const rawCareNeedType = existingSchedule?.careNeedType;
        const careNeedTypeToSave = isSchoolInSchool
          ? rawCareNeedType
            ? { code: rawCareNeedType?.code ?? rawCareNeedType }
            : undefined
          : { code: 'NON_SCHOOL' };
        const savedSchedule = await familyService.saveChildSchedule(application.id, currentChild.id, scheduleId, {
          ...(existingSchedule || {}),
          careNeedType: careNeedTypeToSave,
          days: values.days.map((dayOfWeek) => {
            const existingDay = existingSchedule?.days?.find((d) => d.dayOfWeek === dayOfWeek) || {};
            return { ...existingDay, dayOfWeek };
          }),
        });
        setScheduleId(savedSchedule.id);

        const updatedChildren = [...(application.children || [])];
        updatedChildren[currentChildIndex] = {
          ...currentChild,
          schedules: [savedSchedule, ...(currentChild.schedules || []).filter((s) => s.id !== savedSchedule.id)],
        };
        const updatedApplication = { ...application, children: updatedChildren };
        const finalApplication = await familyService.saveApplication(updatedApplication);
        setApplication(finalApplication);

        _goToNextStep(finalApplication);
      } catch (error) {
        console.error('Error saving schedule of care:', error);
      } finally {
        setSubmitting(false);
      }
    },
    [application, currentChild, currentChildIndex, readOnly, scheduleId, setApplication, setStep, stepConfig],
  );

  if (loading) {
    return (
      <div className="p-8">
        <Spin className="table m-auto" />
      </div>
    );
  }

  return (
    <Spin spinning={submitting}>
      <PageMeta title={`${childFirstName} - Schedule of Care`} />

      <Form form={form} onFinish={handleSubmit} layout="vertical" disabled={readOnly}>
        <div className="p-7 pt-0">
          <div className="space-y-6">
            <Banner question={bannerQuestion} guidance="Select all days that apply" className="my-12" />

            <Form.Item
              name="days"
              rules={[
                {
                  validator: (_, value) =>
                    Array.isArray(value) && value.length > 0
                      ? Promise.resolve()
                      : Promise.reject(new Error('Please select at least one day')),
                },
              ]}
            >
              <RadioCards options={DAYS_OF_WEEK_OPTIONS} multiple allowSelectAll data-testid="schedule-of-care-days" />
            </Form.Item>
          </div>
        </div>

        <ApplicationWizardActions
          applicationIsReadOnly={readOnly}
          backButtonProps={{
            onClick: () => {
              const previousStep = getPreviousStep(
                FAMILY_APPLICATION_STEPS.CHILDREN_SCHEDULE_OF_CARE,
                application,
                stepConfig,
                currentChildIndex,
              );
              setStep({ applicationId: application?.id, newStep: previousStep });
            },
            disabled: submitting,
            'data-testid': 'children-schedule-of-care-back-button',
          }}
          nextButtonProps={{
            loading: submitting,
            'data-testid': 'children-schedule-of-care-next-button',
          }}
        />
      </Form>
    </Spin>
  );
}
